import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MapPin, Mail, Phone } from "lucide-react";
import Logo from "./Logo";

const LINKEDIN_URL = "https://www.linkedin.com/company/elitesoft-iq/posts/?feedView=all";

/** LinkedIn brand glyph (lucide-react no longer ships brand icons) */
const LinkedInIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 1 1 0-4.125 2.062 2.062 0 0 1 0 4.125zM7.119 20.452H3.554V9h3.565v11.452z" />
  </svg>
);

const Footer = () => {
  const { t } = useTranslation();

  /** Each solution links to the section that actually documents it. */
  const services = [
    { key: "digital_onboarding", to: "/services#digital-onboarding" },
    { key: "payment_integration", to: "/services#payment-integration" },
    { key: "e_banking_platforms", to: "/services#ebanking" },
    { key: "kyc_solutions", to: "/#ekyc" },
    { key: "digital_voucher_stores", to: "/services#value-added" },
  ];

  const quickLinks = [
    { label: t("home.title"), to: "/" },
    { label: t("services.title"), to: "/services" },
    { label: t("home.about-us"), to: "/about" },
    { label: t("footer.contact_us"), to: "/contact" },
  ];

  return (
    <footer className="bg-brand pt-20 pb-10 px-6 sm:px-8 text-white">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 lg:gap-8 mb-16">
          {/* Brand + tagline */}
          <div className="lg:col-span-2">
            <div className="mb-6">
              <Logo variant="light" />
            </div>
            <p className="text-white/60 mb-8 max-w-sm leading-relaxed">
              {t("footer.stay_updated_on_our_latest_innovations_and_solutions_through")}{" "}
              LinkedIn
            </p>
            <div className="flex gap-4">
              <a
                href={LINKEDIN_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-lime hover:text-brand transition-colors"
              >
                <LinkedInIcon className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Solutions */}
          <div>
            <h4 className="font-extrabold mb-6 text-white">{t("footer.our_services")}</h4>
            <ul className="space-y-4 text-white/60 text-sm">
              {services.map((service) => (
                <li key={service.key}>
                  <Link to={service.to} className="hover:text-white transition-colors">
                    {t(`footer.${service.key}`)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick links */}
          <div>
            <h4 className="font-extrabold mb-6 text-white">{t("footer.quick_links")}</h4>
            <ul className="space-y-4 text-white/60 text-sm">
              {quickLinks.map((item) => (
                <li key={item.to}>
                  <Link to={item.to} className="hover:text-white transition-colors">
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact info */}
          <div>
            <h4 className="font-extrabold mb-6 text-white">{t("footer.contact_information")}</h4>
            <ul className="space-y-4 text-white/60 text-sm">
              <li className="flex flex-col gap-1">
                <span className="flex items-center gap-2 font-semibold text-white/90 text-xs uppercase tracking-wide">
                  <MapPin className="w-3.5 h-3.5 text-lime" /> {t("footer.location")}
                </span>
                <a
                  href="https://www.google.com/maps/place/33%C2%B017'18.4%22N+44%C2%B023'19.3%22E/@33.2880869,44.3886085,18.17z"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-white transition-colors"
                >
                  Baghdad, Iraq — Al-Jadriah, District 512, H 1/11
                </a>
              </li>
              <li className="flex flex-col gap-1">
                <span className="flex items-center gap-2 font-semibold text-white/90 text-xs uppercase tracking-wide">
                  <Mail className="w-3.5 h-3.5 text-lime" /> {t("footer.email")}
                </span>
                <a href="mailto:info@elitesoft.com" className="hover:text-white transition-colors">
                  info@elitesoft.com
                </a>
              </li>
              <li className="flex flex-col gap-1">
                <span className="flex items-center gap-2 font-semibold text-white/90 text-xs uppercase tracking-wide">
                  <Phone className="w-3.5 h-3.5 text-lime" /> {t("footer.phone")}
                </span>
                <a href="tel:009647709407280" className="self-start hover:text-white transition-colors" dir="ltr">
                  +964 770 940 7280
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-white/40">
          <div>© {new Date().getFullYear()} EliteSoft. All rights reserved.</div>
          <div className="flex gap-6">
            <Link to="/privacy-policy" className="hover:text-white transition-colors">
              {t("footer.privacy_policy")}
            </Link>
            <Link to="/terms-conditions" className="hover:text-white transition-colors">
              {t("footer.terms_and_conditions")}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
